import React from 'react';
import { FileText, FileCheck, Clock, Brain } from 'lucide-react';
import { AdminStatCard } from './AdminStatCard';
import type { OrganizationStatistics } from '@/lib/api/statistics';

interface AdminConsultationsTabProps {
  statistics: OrganizationStatistics | null;
  isLoading: boolean;
}

export const AdminConsultationsTab: React.FC<AdminConsultationsTabProps> = ({
  statistics,
  isLoading,
}) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <AdminStatCard
          icon={<FileText className="h-6 w-6" />}
          title="Total"
          value={statistics?.consultations.total || 0}
          isLoading={isLoading}
        />
        <AdminStatCard
          icon={<FileCheck className="h-6 w-6 text-green-600" />}
          title="Completed"
          value={statistics?.consultations.completed || 0}
          isLoading={isLoading}
        />
        <AdminStatCard
          icon={<Clock className="h-6 w-6 text-yellow-600" />}
          title="In Progress"
          value={statistics?.consultations.inProgress || 0}
          isLoading={isLoading}
        />
        <AdminStatCard
          icon={<Brain className="h-6 w-6 text-purple-600" />}
          title="With AI"
          value={statistics?.consultations.withAI || 0}
          isLoading={isLoading}
        />
        <AdminStatCard
          icon={<FileText className="h-6 w-6 text-gray-600" />}
          title="Without AI"
          value={statistics?.consultations.withoutAI || 0}
          isLoading={isLoading}
        />
      </div>
      {statistics?.consultations.recentConsultations &&
        statistics.consultations.recentConsultations.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-4 py-3">
              <h3 className="font-hanken text-sm font-semibold text-gray-700">
                Recent Consultations
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px] border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="whitespace-nowrap px-4 py-2.5 text-left align-middle font-hanken text-xs font-medium text-gray-600">
                      Patient
                    </th>
                    <th className="whitespace-nowrap px-4 py-2.5 text-left align-middle font-hanken text-xs font-medium text-gray-600">
                      Practitioner
                    </th>
                    <th className="whitespace-nowrap px-4 py-2.5 text-left align-middle font-hanken text-xs font-medium text-gray-600">
                      Date
                    </th>
                    <th className="whitespace-nowrap px-4 py-2.5 text-left align-middle font-hanken text-xs font-medium text-gray-600">
                      Status
                    </th>
                    <th className="whitespace-nowrap px-4 py-2.5 text-left align-middle font-hanken text-xs font-medium text-gray-600">
                      AI Used
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {statistics.consultations.recentConsultations.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 align-middle font-hanken text-sm text-gray-900">
                        {c.patientName}
                      </td>
                      <td className="px-4 py-2.5 align-middle font-hanken text-sm text-gray-600">
                        {c.practitionerName}
                      </td>
                      <td className="px-4 py-2.5 align-middle font-hanken text-sm text-gray-600">
                        {new Date(c.date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2.5 align-middle">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                            c.status === 'finished'
                              ? 'bg-green-100 text-green-800'
                              : c.status === 'in-progress'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {c.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 align-middle">
                        {c.withAI ? (
                          <Brain className="h-4 w-4 text-purple-600" />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  );
};
